import MenuItem from "@mui/material/MenuItem";
import Select, {
  type SelectChangeEvent,
  type SelectProps,
} from "@mui/material/Select";
import type {
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid";
import type { HTMLAttributes, ReactNode } from "react";

const MENU_MAX_HEIGHT = 320;

const SELECT_MENU_PROPS = {
  slotProps: {
    paper: {
      sx: {
        maxHeight: MENU_MAX_HEIGHT,
      },
    },
  },
};

export interface SelectOption {
  value: string;
  label: string;
}

type TestableGridRenderEditCellParams<Row extends GridValidRowModel> =
  GridRenderEditCellParams<Row, string | number> & {
    dataTestId?: string;
  };

function resolveCellValue(value: string | number | null | undefined) {
  let resolvedValue = "";
  if (value === undefined) {
    return resolvedValue;
  }
  if (value === null) {
    return resolvedValue;
  }
  resolvedValue = String(value);
  return resolvedValue;
}

export function createSelectViewCellRenderer<
  Row extends GridValidRowModel,
  Option extends SelectOption,
>(options: Map<string, Option>, renderOption: (option: Option) => ReactNode) {
  function SelectViewCellRenderer(
    params: GridRenderCellParams<Row, string | number>,
  ) {
    const rawValue = params.value;
    if (rawValue === undefined || rawValue === null) {
      return null;
    }
    const key = String(rawValue);
    const option = options.get(key);
    if (!option) {
      return rawValue;
    }
    return renderOption(option);
  }

  return SelectViewCellRenderer;
}

export function createSelectEditCellRenderer<
  Row extends GridValidRowModel,
  Option extends SelectOption,
>(options: Option[], renderOption: (option: Option) => ReactNode) {
  function SelectEditCellRenderer(
    params: TestableGridRenderEditCellParams<Row>,
  ) {
    const currentValue = resolveCellValue(params.value);
    const dataTestId = params.dataTestId;
    const dataTestProps = dataTestId
      ? ({
          SelectDisplayProps: {
            "data-testid": dataTestId,
          } as HTMLAttributes<HTMLDivElement>,
          inputProps: {
            "data-testid": `${dataTestId}-input`,
          },
        } satisfies Pick<
          SelectProps<string>,
          "SelectDisplayProps" | "inputProps"
        >)
      : {};
    const getOptionDataTestId = (value: string) =>
      `select-option-${value.length > 0 ? value : "empty"}`;

    const handleChange = (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value;
      params.api.setEditCellValue(
        { id: params.id, field: params.field, value: newValue },
        event,
      );
      params.api.stopCellEditMode({ id: params.id, field: params.field });
    };

    return (
      <Select
        fullWidth
        value={currentValue}
        onChange={handleChange}
        MenuProps={SELECT_MENU_PROPS}
        {...dataTestProps}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            data-testid={getOptionDataTestId(option.value)}
          >
            {renderOption(option)}
          </MenuItem>
        ))}
      </Select>
    );
  }

  return SelectEditCellRenderer;
}
